import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

export type AiSeoConfig = {
  enabled: boolean;
  target_persona: string;
  search_intent: string;
  content_tone: string;
  semantic_entities: string[];
  related_keywords: string[];
  internal_links: { label: string; url: string }[];
  ai_summary: string;
  ai_generated_at: string | null;
};

export const EMPTY_PHASE5: AiSeoConfig = {
  enabled: false,
  target_persona: "",
  search_intent: "",
  content_tone: "",
  semantic_entities: [],
  related_keywords: [],
  internal_links: [],
  ai_summary: "",
  ai_generated_at: null,
};

type Props = {
  value: AiSeoConfig;
  onChange: (v: AiSeoConfig) => void;
  listingContext?: {
    title?: string;
    neighborhood?: string;
    description?: string;
    bedrooms?: number;
    area_m2?: number;
    price?: number;
    property_type?: string;
  };
  onAiFillMeta?: (meta: { meta_title?: string; meta_description?: string; keywords?: string }) => void;
  onAiFillFaqs?: (faqs: { question: string; answer: string }[]) => void;
};

export default function Phase5Form({ value, onChange, listingContext, onAiFillMeta, onAiFillFaqs }: Props) {
  const v = value || EMPTY_PHASE5;
  const [loading, setLoading] = useState(false);

  const patch = (p: Partial<AiSeoConfig>) => onChange({ ...v, ...p });

  const generate = async () => {
    if (!listingContext?.title) {
      toast.error("Preencha ao menos o título do imóvel antes de gerar.");
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-seo", {
        body: { listing: listingContext, persona: v.target_persona, tone: v.content_tone },
      });
      if (error) throw error;
      const r = data as any;
      patch({
        semantic_entities: r.semantic_entities || [],
        related_keywords: r.related_keywords || [],
        internal_links: r.internal_links || [],
        ai_summary: r.ai_summary || "",
        target_persona: v.target_persona || r.target_persona || "",
        search_intent: v.search_intent || r.search_intent || "",
        content_tone: v.content_tone || r.content_tone || "",
        ai_generated_at: new Date().toISOString(),
        enabled: true,
      });
      if (onAiFillMeta) {
        onAiFillMeta({
          meta_title: r.meta_title,
          meta_description: r.meta_description,
          keywords: r.keywords,
        });
      }
      if (onAiFillFaqs && Array.isArray(r.faqs) && r.faqs.length) {
        onAiFillFaqs(
          r.faqs
            .filter((f: any) => f?.question && f?.answer)
            .map((f: any) => ({ question: String(f.question), answer: String(f.answer) })),
        );
      }
      toast.success("SEO semântico gerado com IA!");
    } catch (e: any) {
      toast.error(e?.message || "Falha ao gerar com IA");
    } finally {
      setLoading(false);
    }
  };

  const list = (arr: string[]) => arr.join(", ");
  const parseList = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);

  return (
    <details className="border border-border rounded-md" open={v.enabled}>
      <summary className="cursor-pointer select-none px-3 py-2 font-body font-semibold text-sm text-foreground">
        🧠 Fase 5 — IA & SEO Semântico
      </summary>
      <div className="p-3 space-y-4">
        <div className="flex items-center justify-between gap-3 bg-muted/40 p-3 rounded">
          <div className="text-xs text-muted-foreground">
            Gere entidades semânticas, palavras-chave relacionadas, meta tags e links internos com IA.
          </div>
          <Button type="button" onClick={generate} disabled={loading} size="sm">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            Gerar com IA
          </Button>
        </div>

        {/* 🎯 Destaque: Resumo para IA (AEO) */}
        <div className="rounded-md border-2 border-primary/40 bg-primary/5 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-lg">🎯</span>
            <Label className="text-sm font-semibold text-foreground">Resumo para IA (ChatGPT, Perplexity, Gemini)</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            2–3 frases factuais que respondem "o que é este imóvel?". Este texto é indexado, exibido na página e enviado como <code>abstract</code> no Schema.org — é o principal insumo para motores de resposta (AEO).
          </p>
          <Textarea
            value={v.ai_summary}
            onChange={(e) => patch({ ai_summary: e.target.value })}
            placeholder="Ex.: Apartamento de 3 quartos e 216 m² com vista frontal para a Lagoa Rodrigo de Freitas, na Zona Sul do Rio. Reformado, 1 vaga, próximo a Ipanema, Leblon e Jardim Botânico."
            rows={4}
            className="bg-background"
          />
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Persona-alvo</Label>
            <Input value={v.target_persona} onChange={(e) => patch({ target_persona: e.target.value })} placeholder="Ex.: Família alta renda 40-60 anos" />
          </div>
          <div>
            <Label className="text-xs">Intenção de busca</Label>
            <Input value={v.search_intent} onChange={(e) => patch({ search_intent: e.target.value })} placeholder="Ex.: Comprar apto Lagoa vista" />
          </div>
          <div>
            <Label className="text-xs">Tom de conteúdo</Label>
            <Input value={v.content_tone} onChange={(e) => patch({ content_tone: e.target.value })} placeholder="Ex.: Sofisticado, consultivo" />
          </div>
        </div>

        <div>
          <Label className="text-xs">Entidades semânticas (separadas por vírgula)</Label>
          <Textarea
            value={list(v.semantic_entities)}
            onChange={(e) => patch({ semantic_entities: parseList(e.target.value) })}
            placeholder="Lagoa Rodrigo de Freitas, Zona Sul do Rio, Cristo Redentor, Ipanema, Corcovado"
            rows={2}
          />
        </div>

        <div>
          <Label className="text-xs">Palavras-chave relacionadas (separadas por vírgula)</Label>
          <Textarea
            value={list(v.related_keywords)}
            onChange={(e) => patch({ related_keywords: parseList(e.target.value) })}
            placeholder="apartamento vista lagoa, imóvel de luxo zona sul rio, cobertura ipanema"
            rows={2}
          />
        </div>


        <div className="space-y-2">
          <Label className="text-xs">Links internos sugeridos</Label>
          {v.internal_links.map((link, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
              <Input
                value={link.label}
                onChange={(e) => {
                  const next = [...v.internal_links];
                  next[i] = { ...next[i], label: e.target.value };
                  patch({ internal_links: next });
                }}
                placeholder="Título do link"
              />
              <Input
                value={link.url}
                onChange={(e) => {
                  const next = [...v.internal_links];
                  next[i] = { ...next[i], url: e.target.value };
                  patch({ internal_links: next });
                }}
                placeholder="/lagoa ou URL completa"
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => patch({ internal_links: v.internal_links.filter((_, j) => j !== i) })}>
                Remover
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => patch({ internal_links: [...v.internal_links, { label: "", url: "" }] })}
          >
            + Adicionar link
          </Button>
        </div>

        {v.ai_generated_at && (
          <p className="text-xs text-muted-foreground">
            Última geração por IA: {new Date(v.ai_generated_at).toLocaleString("pt-BR")}
          </p>
        )}
      </div>
    </details>
  );
}
